import { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Layers,
  TrendingDown,
  Landmark,
  ShieldAlert,
  LineChart,
  FileSignature,
  Droplets,
  Zap,
  Stamp,
  ArrowUpRight,
  Building2,
  Handshake,
  CheckCircle2,
  Menu,
  X,
  Phone,
  Mail,
  Globe,
  MapPin,
  ArrowRight,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { LOGO_URL, IMG, CONTACT, FACTORS, NAV_LINKS, WHATSAPP_LINK, TARANTELLA_LOGO, LALO_IMG, LALO_RENDER, LALO_OBRA, CLIENTS } from "@/pages/data";
import ChatWidget from "@/pages/ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsappIcon = ({ size = 20, className = "" }) => (
  <svg
    viewBox="0 0 32 32"
    width={size}
    height={size}
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16.003 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.257.59 4.46 1.71 6.4L3.2 28.8l6.57-1.72a12.74 12.74 0 0 0 6.23 1.6h.005c7.06 0 12.8-5.74 12.8-12.8 0-3.42-1.332-6.635-3.752-9.05A12.71 12.71 0 0 0 16.003 3.2zm0 23.36h-.004a10.58 10.58 0 0 1-5.39-1.476l-.387-.23-4.01 1.05 1.07-3.91-.252-.4a10.55 10.55 0 0 1-1.62-5.624c0-5.86 4.77-10.63 10.64-10.63 2.84 0 5.51 1.108 7.52 3.118a10.56 10.56 0 0 1 3.114 7.52c0 5.86-4.77 10.63-10.63 10.63zm5.83-7.96c-.32-.16-1.89-.93-2.18-1.04-.29-.106-.5-.16-.71.16-.21.32-.815 1.04-1 1.253-.184.213-.368.24-.688.08-.32-.16-1.35-.498-2.57-1.586-.95-.847-1.59-1.893-1.776-2.213-.184-.32-.02-.493.14-.652.144-.143.32-.373.48-.56.16-.187.213-.32.32-.533.106-.213.053-.4-.027-.56-.08-.16-.71-1.71-.974-2.343-.256-.613-.516-.53-.71-.54l-.605-.01c-.21 0-.55.08-.84.4-.29.32-1.1 1.077-1.1 2.627 0 1.55 1.127 3.046 1.284 3.26.16.213 2.22 3.39 5.38 4.753.752.325 1.34.52 1.797.665.755.24 1.443.206 1.987.125.606-.09 1.89-.773 2.156-1.52.266-.747.266-1.387.187-1.52-.08-.133-.29-.213-.61-.373z" />
  </svg>
);

const ICONS = {
  Layers,
  TrendingDown,
  Landmark,
  ShieldAlert,
  LineChart,
  FileSignature,
  Droplets,
  Zap,
  Stamp,
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ---------------- Counter ---------------- */
function CountUp({ to = 227, duration = 1600 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) {
      setValue(0);
      return;
    }
    let start;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * to));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return <span ref={ref}>{value}</span>;
}

function Counter() {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const DURATION = 1600;
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!inView) {
      setDone(false);
      return;
    }
    const t = setTimeout(() => setDone(true), DURATION + 60);
    return () => clearTimeout(t);
  }, [inView]);

  const CELEBRATE = ["👍", "🙂", "❤️"];

  return (
    <section
      ref={ref}
      data-testid="counter-section"
      className="bg-foreground text-white py-20 md:py-28 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-30" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-white/60">
          <span className="h-px w-8 bg-primary" />
          Resultados que dan tranquilidad
          <span className="h-px w-8 bg-primary" />
        </span>

        <div className="mt-6 flex items-center justify-center gap-3">
          <p
            data-testid="counter-value"
            className="font-heading font-extrabold text-7xl sm:text-8xl lg:text-9xl tracking-tighter text-primary leading-none"
          >
            <CountUp to={227} duration={DURATION} />
          </p>

          <div className="relative w-10 sm:w-14 self-stretch">
            {done &&
              CELEBRATE.map((e, i) => (
                <motion.span
                  key={e}
                  aria-hidden="true"
                  initial={{ opacity: 0, y: 18, scale: 0.5 }}
                  animate={{ opacity: [0, 1, 1, 0], y: -34, scale: 1 }}
                  transition={{ duration: 2, delay: i * 0.22, ease: "easeOut" }}
                  style={{ top: `${20 + i * 28}%`, fontSize: "1.9rem" }}
                  className="absolute left-0 select-none pointer-events-none"
                >
                  {e}
                </motion.span>
              ))}
          </div>
        </div>

        <p className="mt-5 text-lg md:text-xl text-white/75 max-w-xl mx-auto">
          Clientes contentos!
        </p>
      </div>
    </section>
  );
}

/* ---------------- Navbar ---------------- */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-border/70 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("top")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <span className="font-heading font-extrabold text-2xl tracking-tighter leading-none">
            BM
          </span>
          <span className="hidden sm:flex flex-col leading-none border-l border-foreground/20 pl-3">
            <span className="text-[11px] tracking-[0.28em] font-medium text-foreground">
              GRUPO
            </span>
            <span className="text-[11px] tracking-[0.28em] text-muted-foreground">
              FINANCIERO
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            className="lg:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menú"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-b border-border px-6 py-4 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => {
                scrollTo(l.id);
                setOpen(false);
              }}
              className="text-left py-3 text-sm text-foreground border-b border-border/60 last:border-0"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-white"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.10]"
        style={{ backgroundImage: `url(${IMG.heroArch})` }}
      />
      <div className="absolute inset-0 grain-overlay" />
      <div className="absolute right-0 top-0 h-full w-[42%] bg-primary/5 hidden lg:block" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-20">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="max-w-4xl"
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-foreground/70 mb-6">
            <span className="h-px w-8 bg-primary" />
            Nuevo León · México
          </span>

          <h1 className="font-heading font-medium text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-foreground text-balance">
            Estabilidad financiera para el sector{" "}
            <span className="relative inline-block">
              inmobiliario
              <span className="absolute left-0 -bottom-1 h-3 w-full bg-primary/70 -z-0" />
            </span>{" "}
            de Nuevo León.
          </h1>

          <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
            Ayudamos a desarrolladores, compradores e inversionistas a superar la
            crisis del sector, estructurando soluciones seguras, empáticas y
            definitivas.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              data-testid="hero-cta"
              onClick={() => scrollTo("contacto")}
              size="lg"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-medium h-12 px-8 text-base group"
            >
              Habla con un experto
              <ArrowUpRight
                className="ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                size={18}
              />
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => scrollTo("soluciones")}
              className="rounded-full h-12 px-8 text-base border-foreground/20 hover:bg-foreground hover:text-white"
            >
              Conocer soluciones
            </Button>
          </div>

          <p className="mt-12 font-heading text-xl md:text-2xl tracking-tight text-foreground/90">
            «Tu tranquilidad es nuestro negocio.»
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Intro / Manifiesto ---------------- */
function Intro() {
  return (
    <section
      id="manifiesto"
      data-testid="intro-section"
      className="py-20 md:py-28 bg-white border-b border-border"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div
            data-testid="intro-video"
            className="mb-12 rounded-xl overflow-hidden border border-border bg-foreground shadow-xl"
            style={{ position: "relative", paddingTop: "56.25%" }}
          >
            <iframe
              src="https://player.vimeo.com/video/1210980805?title=0&byline=0&portrait=0"
              title="BM Grupo Financiero"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </div>
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            Nuestro propósito
          </span>
          <div className="mt-7 space-y-6 text-lg md:text-xl leading-relaxed text-foreground/80">
            <p>
              En los últimos años hemos sido testigos de cómo algunos de los
              empresarios más experimentados del sector inmobiliario de Nuevo
              León —personas con décadas de trayectoria, proyectos exitosos y una
              reputación construida con esfuerzo— enfrentaron circunstancias que
              escaparon por completo a su control. Hombres y mujeres de trabajo y
              buena fe que, a pesar de su conocimiento y su voluntad, vieron
              comprometidos proyectos que representaban el patrimonio y la
              confianza de cientos de familias.
            </p>
            <p>
              En BM Grupo Financiero entendemos que detrás de cada proyecto
              inconcluso hay una historia legítima. Por eso nuestra empresa nació
              con un propósito claro y sin ambigüedades: ayudar a la mayor cantidad
              posible de personas a encontrar una salida justa. Desarrolladores que
              necesitan espacio para reorganizarse. Compradores que merecen ver
              materializado lo que pagaron. Inversionistas que confiaron su capital
              y esperan recuperarlo.
            </p>
            <p className="font-heading text-2xl md:text-3xl tracking-tight text-foreground border-l-4 border-primary pl-6">
              Nuevo León no para. Y nosotros tampoco. Nuestra única finalidad es que
              esta ciudad siga en movimiento, que sus proyectos concluyan y que
              quienes invirtieron su confianza y su patrimonio en ella reciban lo
              que merecen.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Mission ---------------- */
function Mission() {
  return (
    <section
      id="quienes-somos"
      data-testid="mission-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            Quiénes somos
          </span>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-5 text-balance">
            Creamos valor a partir de situaciones complejas.
          </h2>
          <p className="mt-7 text-lg text-muted-foreground leading-relaxed">
            BM Grupo Financiero nació de una realidad concreta: cuando las
            crisis llegan, arrastran el esfuerzo, el patrimonio y los sueños de
            miles de personas. Identificamos en ello la oportunidad de hacer las
            cosas de manera diferente.
          </p>
          <div className="mt-8 border-l-4 border-primary pl-6">
            <p className="text-xl text-foreground font-medium leading-snug">
              Nuestra misión: ayudar a los desarrolladores a resolver sus
              compromisos y devolver la tranquilidad a quienes confiaron su
              patrimonio.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
            {["Soluciones reales", "Trabajo serio", "Beneficio para todas las partes"].map(
              (t) => (
                <span key={t} className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 size={18} className="text-primary" /> {t}
                </span>
              )
            )}
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="relative"
        >
          <div className="absolute -top-5 -left-5 h-24 w-24 border-t-4 border-l-4 border-primary hidden md:block" />
          <img
            src={IMG.missionTrust}
            alt="Reunión profesional de BM Grupo Financiero"
            className="w-full h-[480px] object-cover grayscale-[15%]"
          />
          <div className="absolute -bottom-6 -right-6 bg-foreground text-white p-6 max-w-[240px] hidden md:block">
            <p className="font-heading text-2xl tracking-tight">No prometemos milagros.</p>
            <p className="text-sm text-white/70 mt-2">
              Pero sí que tu situación tiene una salida.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Context / Crisis factors ---------------- */
function Context() {
  return (
    <section
      id="contexto"
      data-testid="context-section"
      className="py-24 md:py-32 bg-secondary/60"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            El contexto
          </span>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-5 text-balance">
            Por qué colapsó el sector.
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Para entender qué hacemos, es necesario entender qué ocurrió. Nueve
            factores coincidieron en el tiempo y transformaron un auge
            extraordinario en una de las crisis más complejas del sector.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FACTORS.map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <motion.div
                key={f.title}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                data-testid={`factor-card-${i}`}
                className="group bg-white border border-border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primary/60"
              >
                <div className="flex items-center justify-between">
                  <div className="h-12 w-12 rounded-md bg-secondary flex items-center justify-center group-hover:bg-primary transition-colors">
                    <Icon size={22} className="text-foreground" />
                  </div>
                  <span className="font-heading text-sm text-muted-foreground">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-heading text-xl tracking-tight mt-6">{f.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {f.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 border-l-4 border-primary pl-6 max-w-3xl">
          <p className="text-xl md:text-2xl font-heading tracking-tight leading-snug">
            El resultado es una crisis que va más allá de los números: miles de
            familias y empresarios de buena fe quedaron atrapados. Y es ahí donde
            entra BM Grupo Financiero.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Solutions ---------------- */
function SolutionCard({ tag, icon: Icon, title, points, testid }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      data-testid={testid}
      className="bg-white border border-border p-8 md:p-12 h-full flex flex-col"
    >
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-md bg-primary flex items-center justify-center">
          <Icon size={22} className="text-primary-foreground" />
        </div>
        <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
          {tag}
        </span>
      </div>
      <h3 className="font-heading font-medium text-2xl md:text-3xl tracking-tight mt-7 leading-tight">
        {title}
      </h3>
      <ul className="mt-7 space-y-5 flex-1">
        {points.map((p, idx) => (
          <li key={idx} className="flex gap-4">
            <CheckCircle2 size={22} className="text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground leading-relaxed">{p}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function Solutions() {
  return (
    <section
      id="soluciones"
      data-testid="solutions-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            Soluciones
          </span>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-5 text-balance">
            Soluciones concretas para cada parte.
          </h2>
        </div>

        <div
          data-testid="que-hacemos-video"
          className="mb-14 rounded-xl overflow-hidden border border-border bg-foreground shadow-xl"
          style={{ position: "relative", paddingTop: "56.25%" }}
        >
          <iframe
            src="https://player.vimeo.com/video/1207530547?title=0&byline=0&portrait=0"
            title="¿Qué hacemos? — BM Grupo Financiero"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <SolutionCard
            testid="solutions-devs-section"
            tag="Para desarrolladores"
            icon={Building2}
            title="Resolvemos compromisos que hoy parecen imposibles."
            points={[
              "Adquirimos los proyectos inmobiliarios que no pudieron concluirse, relevando al desarrollador de sus obligaciones y dando continuidad al activo.",
              "Cuando es posible, negociamos esquemas de pago a largo plazo con reducciones de hasta el 80% del pasivo total.",
            ]}
          />
          <SolutionCard
            testid="solutions-buyers-section"
            tag="Para compradores e inversionistas"
            icon={Handshake}
            title="Convertimos lo incobrable en acceso real."
            points={[
              "Aceptamos como forma de pago derechos de crédito, derechos fideicomisarios, documentos litigiosos o garantías de cobro futuro.",
              "Hasta el 40% del valor de un nuevo inmueble de nuestro portafolio puede cubrirse con esos instrumentos; el resto, con flujo corriente.",
            ]}
          />
        </div>
      </div>
    </section>
  );
}

/* ---------------- Stats ---------------- */
function Stats() {
  const stats = [
    { value: "80%", label: "Reducción máxima de pasivo" },
    { value: "40%", label: "Del valor cubierto con derechos" },
    { value: "100%", label: "Recursos garantizados para terminar" },
  ];
  return (
    <section data-testid="stats-section" className="bg-foreground text-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid sm:grid-cols-3 gap-12">
        {stats.map((s, i) => (
          <motion.div
            key={s.value}
            custom={i}
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            className="text-center sm:text-left"
          >
            <p className="font-heading font-extrabold text-6xl md:text-7xl tracking-tighter text-primary leading-none">
              {s.value}
            </p>
            <p className="mt-4 text-white/70 text-sm uppercase tracking-[0.15em]">
              {s.label}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Model ---------------- */
function Model() {
  const points = [
    {
      n: "01",
      title: "Arranque condicionado",
      desc: "Los proyectos solo inician cuando está garantizado el 100% de los recursos necesarios para concluirlos. No antes. Cada peso comprometido tiene un destino claro y verificable.",
    },
    {
      n: "02",
      title: "Fondeo privado",
      desc: "No trabajamos con apalancamiento bancario en etapas tempranas. Nuestro fondeo proviene de inversionistas privados y de los instrumentos que recibimos como pago.",
    },
    {
      n: "03",
      title: "Cuando todos ganan",
      desc: "Transformamos pasivos en activos y problemas en soluciones, protegiendo a todos los participantes. Porque cuando todos ganan, los proyectos se llenan de vida.",
    },
  ];
  return (
    <section
      id="modelo"
      data-testid="model-section"
      className="py-24 md:py-32 bg-secondary/60"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            Nuestro modelo
          </span>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-5 text-balance">
            Por qué funciona.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
          {points.map((p, i) => (
            <motion.div
              key={p.n}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              className="bg-white p-8 md:p-10"
            >
              <span className="font-heading font-extrabold text-5xl tracking-tighter text-primary">
                {p.n}
              </span>
              <h3 className="font-heading text-xl tracking-tight mt-5">{p.title}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed text-sm">
                {p.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Projects ---------------- */
function Projects() {
  return (
    <section
      id="proyectos"
      data-testid="projects-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            Proyectos en curso
          </span>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-5 text-balance">
            Proyectos que avanzan con certeza gracias al modelo de BM Grupo
            Financiero.
          </h2>
        </div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          data-testid="project-tarantella"
          className="grid lg:grid-cols-2 gap-0 border border-border rounded-md overflow-hidden"
        >
          <a
            href="https://plazatarantella.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tarantella-logo-link"
            aria-label="Visitar el sitio de Plaza Tarantella"
            className="bg-secondary/40 flex items-center justify-center p-12 md:p-16 group cursor-pointer transition-colors hover:bg-secondary/70"
          >
            <img
              src={TARANTELLA_LOGO}
              alt="Plaza Tarantella"
              className="w-full max-w-sm h-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </a>
          <div className="p-10 md:p-14 flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 self-start text-xs font-bold uppercase tracking-[0.15em] text-foreground bg-primary px-3 py-1.5 rounded-full">
              <span className="h-2 w-2 rounded-full bg-foreground animate-pulse" />
              En curso
            </span>
            <h3 className="font-heading font-medium text-3xl tracking-tight mt-6">
              Plaza Tarantella
            </h3>
            <div className="mt-4 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Plaza Tarantella representa un ejemplo tangible de la filosofía y
                el modelo de negocio de BM Grupo Financiero. Es un desarrollo
                concebido, financiado y ejecutado bajo un esquema de planeación
                financiera responsable, con recursos garantizados desde su origen
                y una estrategia orientada a brindar certeza en cada etapa de su
                construcción.
              </p>
              <p>
                Su desarrollo demuestra que es posible transformar situaciones
                complejas en proyectos sólidos, recuperando valor para
                inversionistas, compradores y desarrolladores. Más que una obra en
                construcción, Plaza Tarantella es la evidencia de que una adecuada
                estructura financiera, una administración responsable y una visión
                de largo plazo permiten convertir los desafíos del sector
                inmobiliario en oportunidades reales de crecimiento.
              </p>
              <p>
                Plaza Tarantella es la prueba de que, cuando un proyecto cuenta con
                una planeación adecuada y recursos suficientes para su conclusión,
                la confianza se fortalece y el patrimonio de quienes participan se
                protege.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <div>
                <p className="font-heading font-extrabold text-4xl tracking-tighter text-primary leading-none">
                  100%
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  Recursos garantizados
                </p>
              </div>
              <div>
                <p className="font-heading font-extrabold text-4xl tracking-tighter text-foreground leading-none">
                  En marcha
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  Estado del proyecto
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Planeación (LALO) ---------------- */
function LaloSlideshow() {
  const imgs = [LALO_IMG, LALO_RENDER, LALO_OBRA];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % imgs.length), 3800);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="relative min-h-[320px] lg:min-h-full bg-secondary/40 overflow-hidden">
      <AnimatePresence mode="sync">
        <motion.img
          key={idx}
          src={imgs[idx]}
          alt="LALO La Loma"
          draggable={false}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-full object-cover select-none"
        />
      </AnimatePresence>
      {/* Overlay to suppress browser image-hover buttons (Google Lens, etc.) */}
      <div className="absolute inset-0 z-[5]" aria-hidden="true" />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {imgs.map((_, i) => (
          <button
            key={i}
            aria-label={`Ver imagen ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === idx ? "w-6 bg-primary" : "w-1.5 bg-white/70 hover:bg-white"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function Planeacion() {
  return (
    <section
      id="planeacion"
      data-testid="planeacion-section"
      className="pb-24 md:pb-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-14">
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            Próximos proyectos
          </span>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-5 text-balance">
            Proyectos en planeación de rescate financiero.
          </h2>
        </div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          data-testid="project-lalo"
          className="grid lg:grid-cols-2 gap-0 border border-border rounded-md overflow-hidden items-stretch"
        >
          <LaloSlideshow />

          <div className="p-10 md:p-14 flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 self-start text-xs font-bold uppercase tracking-[0.15em] text-foreground bg-primary px-3 py-1.5 rounded-full">
              <span className="h-2 w-2 rounded-full bg-foreground animate-pulse" />
              En planeación
            </span>
            <h3 className="font-heading font-medium text-3xl tracking-tight mt-6">
              LALO — La Loma
            </h3>
            <div className="mt-4 space-y-4 text-muted-foreground leading-relaxed">
              <p>
                LALO La Loma, desarrollado por Proyectos Nueve, es una torre de
                gran altura que hoy se encuentra en proceso de planeación de
                rescate financiero. Tras un análisis responsable, BM Grupo
                Financiero presentó una oferta de cierre que ya fue aceptada por
                la desarrolladora, dando inicio a la estructuración del plan que
                permitirá concluir la obra.
              </p>
              <p>
                El proyecto contempla un tiempo de ejecución estimado de 24 a 30
                meses para su terminación, con el objetivo de proteger y dar
                certeza a las numerosas personas que invirtieron su patrimonio en
                este inmueble. Nuestro compromiso es transformar una situación
                compleja en un cierre ordenado, transparente y seguro para todas
                las partes.
              </p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <div>
                <p className="font-heading font-extrabold text-4xl tracking-tighter text-primary leading-none">
                  24–30
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  Meses de ejecución
                </p>
              </div>
              <div>
                <p className="font-heading font-extrabold text-4xl tracking-tighter text-foreground leading-none">
                  Oferta aceptada
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  En espera de documentación para iniciar el Due Diligence
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Clients / Colaboraciones ---------------- */
function Clients() {
  return (
    <section
      id="empresas"
      data-testid="clients-section"
      className="py-24 md:py-32 bg-secondary/60"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            Colaboraciones
          </span>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-5 text-balance">
            Empresas con las que hemos colaborado en sus procesos de recuperación
            y reestructuración.
          </h2>
          <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
            <p>
              Durante los últimos años, BM Grupo Financiero ha tenido la
              oportunidad de colaborar —directa o indirectamente— con empresas que
              enfrentaban desafíos financieros, operativos o de continuidad
              derivados de las condiciones del mercado inmobiliario.
            </p>
            <p>
              Nuestro compromiso ha sido diseñar soluciones responsables que les
              permitan recuperar estabilidad, proteger el valor de sus activos y
              generar nuevas oportunidades de crecimiento.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
          {CLIENTS.map((c, i) => (
            <motion.div
              key={c.name}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              data-testid={`client-logo-${i}`}
              className={`border border-border rounded-md h-40 flex items-center justify-center p-6 overflow-hidden ${
                c.dark ? "bg-foreground" : "bg-white"
              }`}
            >
              <img
                src={c.src}
                alt={c.name}
                className={`w-auto object-contain ${
                  c.boost ? "max-h-36 max-w-[95%] scale-110" : "max-h-24 max-w-[80%]"
                }`}
              />
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          data-testid="clients-cta"
          className="mt-16 bg-foreground text-white rounded-md p-10 md:p-16"
        >
          <h3 className="font-heading font-medium text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight text-balance">
            ¿Su empresa enfrenta una situación similar?
          </h3>
          <div className="mt-6 grid lg:grid-cols-2 gap-x-12 gap-y-5 text-white/70 leading-relaxed">
            <p>
              En BM Grupo Financiero entendemos que detrás de cada proyecto existen
              personas, familias, inversionistas y años de esfuerzo.
            </p>
            <p>
              Si su empresa atraviesa un momento complejo, nos gustaría conocer su
              situación y analizar conjuntamente las alternativas disponibles.
            </p>
            <p>
              Permítanos brindarle nuestra experiencia, acompañamiento y una
              solución estructurada que le permita recuperar viabilidad financiera
              y abrir una nueva etapa para su empresa.
            </p>
            <p className="text-primary font-medium">
              En muchas ocasiones, una solución comienza simplemente con una
              conversación.
            </p>
          </div>
          <Button
            data-testid="clients-cta-button"
            onClick={() =>
              document
                .getElementById("contacto")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            size="lg"
            className="mt-9 rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-medium h-12 px-8 text-base group"
          >
            Iniciemos la conversación
            <ArrowUpRight
              className="ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              size={18}
            />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- Contact ---------------- */
const INITIAL = { nombre: "", email: "", telefono: "", perfil: "", mensaje: "" };

function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.telefono || !form.perfil || !form.mensaje) {
      toast.error("Por favor completa todos los campos.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Gracias. Hemos recibido tu mensaje y te contactaremos pronto.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "No se pudo enviar. Intenta de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <span className="text-xs uppercase tracking-[0.22em] font-bold text-foreground/60">
            Contacto
          </span>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-5 text-balance">
            Tu tranquilidad puede regresar antes de lo que crees.
          </h2>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Si eres un desarrollador con compromisos difíciles de resolver, o
            invertiste tu patrimonio en un proyecto que no se materializó, estamos
            aquí para escucharte.
          </p>

          <div className="mt-10 space-y-5">
            {[
              { icon: Phone, label: "Teléfono / WhatsApp", value: CONTACT.telefono },
              { icon: Mail, label: "Correo", value: CONTACT.email },
              { icon: Globe, label: "Sitio web", value: CONTACT.sitio },
              { icon: MapPin, label: "Ubicación", value: CONTACT.ubicacion },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-md border border-border flex items-center justify-center shrink-0">
                  <c.icon size={18} className="text-foreground" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    {c.label}
                  </p>
                  <p className="text-foreground font-medium">{c.value}</p>
                </div>
              </div>
            ))}
          </div>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-button"
            className="mt-8 inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-medium px-7 h-12 transition-colors"
          >
            <WhatsappIcon size={20} />
            Escríbenos por WhatsApp
          </a>

          <div className="mt-10 flex items-center justify-center">
            <img
              src={LOGO_URL}
              alt="BM Financial Group"
              className="w-full max-w-[260px] h-auto object-contain"
            />
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-secondary/50 border border-border p-8 md:p-10 rounded-md"
        >
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre completo</Label>
              <Input
                id="nombre"
                data-testid="input-nombre"
                value={form.nombre}
                onChange={(e) => set("nombre", e.target.value)}
                placeholder="Tu nombre"
                className="bg-white h-12"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="tu@correo.com"
                  className="bg-white h-12"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  data-testid="input-telefono"
                  value={form.telefono}
                  onChange={(e) => set("telefono", e.target.value)}
                  placeholder="+52 ..."
                  className="bg-white h-12"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo de perfil</Label>
              <Select value={form.perfil} onValueChange={(v) => set("perfil", v)}>
                <SelectTrigger data-testid="select-perfil" className="bg-white h-12">
                  <SelectValue placeholder="Selecciona una opción" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desarrollador">Desarrollador</SelectItem>
                  <SelectItem value="comprador">Comprador</SelectItem>
                  <SelectItem value="inversionista">Inversionista</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mensaje">Mensaje</Label>
              <Textarea
                id="mensaje"
                data-testid="input-mensaje"
                value={form.mensaje}
                onChange={(e) => set("mensaje", e.target.value)}
                placeholder="Cuéntanos brevemente tu situación..."
                rows={5}
                className="bg-white resize-none"
              />
            </div>
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              size="lg"
              className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-medium h-12 text-base"
            >
              {loading ? "Enviando..." : "Enviar mensaje"}
              {!loading && <ArrowRight size={18} className="ml-1" />}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Información tratada de forma privada y confidencial.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */
function PrivacyDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="privacy-link"
          className="text-white/60 hover:text-primary transition-colors underline underline-offset-4"
        >
          Aviso de Privacidad
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white text-foreground">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            Aviso de Privacidad
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            BM Grupo Financiero — Última actualización: {new Date().getFullYear()}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 text-sm leading-relaxed text-foreground/80 mt-2">
          <div>
            <h4 className="font-semibold text-foreground mb-1">1. Responsable</h4>
            <p>
              BM Grupo Financiero (en adelante "BM Grupo Financiero"), con domicilio
              en San Pedro Garza García, Nuevo León, México, es responsable del
              tratamiento y protección de sus datos personales, conforme a la Ley
              Federal de Protección de Datos Personales en Posesión de los
              Particulares (LFPDPPP).
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">2. Datos que recabamos</h4>
            <p>
              Podemos recabar: nombre, correo electrónico, número telefónico, perfil
              (desarrollador, comprador o inversionista) e información relacionada con
              su situación o caso, proporcionados de forma voluntaria a través de
              nuestro formulario de contacto o de nuestros canales de comunicación.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">3. Finalidades</h4>
            <p>
              Sus datos se utilizan para: contactarle y dar seguimiento a su
              solicitud, analizar su situación, ofrecerle alternativas y soluciones,
              y brindarle atención personalizada. No utilizamos sus datos con fines
              distintos a los aquí señalados sin su consentimiento.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">4. Asistente virtual</h4>
            <p>
              Las conversaciones con nuestro asistente virtual (Ángel) no se
              registran ni se almacenan en ninguna base de datos, salvo que usted
              decida compartir sus datos mediante el formulario, en cuyo caso serán
              tratados conforme a este aviso.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">5. Transferencia de datos</h4>
            <p>
              No transferimos sus datos personales a terceros sin su consentimiento,
              salvo en los casos previstos por la ley o cuando sea necesario para dar
              cumplimiento a las finalidades antes descritas.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">6. Derechos ARCO</h4>
            <p>
              Usted tiene derecho a Acceder, Rectificar, Cancelar u Oponerse al
              tratamiento de sus datos personales (derechos ARCO), así como a revocar
              su consentimiento. Para ejercerlos, envíe su solicitud al correo{" "}
              <span className="text-foreground font-medium">
                contacto@bmgrupofinanciero.com
              </span>
              .
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-foreground mb-1">7. Cambios al aviso</h4>
            <p>
              Este aviso de privacidad puede sufrir modificaciones. Cualquier cambio
              será publicado en este mismo sitio web.
            </p>
          </div>
          <p className="text-xs text-muted-foreground pt-2 border-t border-border">
            Para dudas sobre este aviso, contáctenos en contacto@bmgrupofinanciero.com
            o al +52 81 2667 2407.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ArcoDialog() {
  const DERECHOS = {
    acceso: "Acceso",
    rectificacion: "Rectificación",
    cancelacion: "Cancelación",
    oposicion: "Oposición",
  };
  const [f, setF] = useState({
    nombre: "",
    contacto: "",
    derecho: "",
    descripcion: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const buildText = () =>
    `SOLICITUD DE DERECHOS ARCO — BM GRUPO FINANCIERO\n\n` +
    `Nombre del titular: ${f.nombre}\n` +
    `Medio de contacto (correo/teléfono): ${f.contacto}\n` +
    `Derecho que desea ejercer: ${DERECHOS[f.derecho] || ""}\n` +
    `Descripción de la solicitud:\n${f.descripcion}\n\n` +
    `Fecha: ${new Date().toLocaleDateString("es-MX")}\n` +
    `Enviar a: contacto@bmgrupofinanciero.com`;

  const valid = f.nombre.trim() && f.contacto.trim() && f.derecho && f.descripcion.trim();

  const enviarCorreo = () => {
    const subject = encodeURIComponent("Solicitud de Derechos ARCO");
    const body = encodeURIComponent(buildText());
    window.location.href = `mailto:contacto@bmgrupofinanciero.com?subject=${subject}&body=${body}`;
  };

  const descargar = () => {
    const blob = new Blob([buildText()], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "solicitud-arco-bm-grupo-financiero.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="arco-link"
          className="text-white/60 hover:text-primary transition-colors underline underline-offset-4"
        >
          Solicitud ARCO
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white text-foreground">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            Solicitud de Derechos ARCO
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Complete sus datos para ejercer sus derechos de Acceso, Rectificación,
            Cancelación u Oposición. Puede enviarla por correo o descargarla.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="arco-nombre">Nombre completo del titular</Label>
            <Input
              id="arco-nombre"
              data-testid="arco-nombre"
              value={f.nombre}
              onChange={(e) => set("nombre", e.target.value)}
              placeholder="Su nombre"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="arco-contacto">Correo o teléfono de contacto</Label>
            <Input
              id="arco-contacto"
              data-testid="arco-contacto"
              value={f.contacto}
              onChange={(e) => set("contacto", e.target.value)}
              placeholder="correo@ejemplo.com / +52 ..."
            />
          </div>
          <div className="space-y-2">
            <Label>Derecho que desea ejercer</Label>
            <Select value={f.derecho} onValueChange={(v) => set("derecho", v)}>
              <SelectTrigger data-testid="arco-derecho">
                <SelectValue placeholder="Seleccione una opción" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="acceso">Acceso</SelectItem>
                <SelectItem value="rectificacion">Rectificación</SelectItem>
                <SelectItem value="cancelacion">Cancelación</SelectItem>
                <SelectItem value="oposicion">Oposición</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="arco-desc">Descripción de la solicitud</Label>
            <Textarea
              id="arco-desc"
              data-testid="arco-descripcion"
              value={f.descripcion}
              onChange={(e) => set("descripcion", e.target.value)}
              placeholder="Describa brevemente su solicitud..."
              rows={4}
              className="resize-none"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              data-testid="arco-enviar"
              onClick={enviarCorreo}
              disabled={!valid}
              className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/85 font-medium"
            >
              <Mail size={16} className="mr-1.5" /> Enviar por correo
            </Button>
            <Button
              data-testid="arco-descargar"
              onClick={descargar}
              disabled={!valid}
              variant="outline"
              className="flex-1 rounded-full"
            >
              Descargar solicitud
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Su solicitud se dirige a contacto@bmgrupofinanciero.com
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Footer() {
  return (
    <footer data-testid="footer" className="relative bg-foreground text-white overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-white/60 max-w-md leading-relaxed">
              BM Grupo Financiero — Creamos valor a partir de situaciones
              complejas, generando soluciones que benefician a todas las partes.
            </p>
          </div>
          <div className="md:text-right">
            <p className="font-heading text-2xl tracking-tight text-primary">
              «Tu tranquilidad es nuestro negocio.»
            </p>
            <div className="mt-5 flex flex-wrap md:justify-end gap-x-6 gap-y-2">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="text-sm text-white/70 hover:text-primary transition-colors"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/50">
          <span>© {new Date().getFullYear()} BM Grupo Financiero.</span>
          <div className="flex items-center gap-4">
            <PrivacyDialog />
            <span className="text-white/30">·</span>
            <ArcoDialog />
            <span className="hidden sm:inline">Nuevo León, México.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------------- Page ---------------- */
function FloatingWhatsApp() {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp"
      aria-label="Escríbenos por WhatsApp"
      className="fixed left-5 bottom-5 z-50 h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white flex items-center justify-center shadow-lg shadow-black/20 transition-transform hover:scale-105"
    >
      <WhatsappIcon size={30} />
    </a>
  );
}

export default function Landing() {
  return (
    <div className="bg-white text-foreground antialiased overflow-x-hidden">
      <Navbar />
      <main>
        <Hero />
        <Intro />
        <Mission />
        <Counter />
        <Context />
        <Solutions />
        <Stats />
        <Model />
        <Projects />
        <Clients />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <ChatWidget />
    </div>
  );
}
